import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

function formatApiError(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// Helper to make auth requests using XMLHttpRequest to bypass Emergent's fetch interceptor
// which consumes response body for error logging, causing "body stream already read" errors
function authRequest(url, options = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    xhr.withCredentials = true;
    
    if (options.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
    }
    
    xhr.onload = function() {
      let data = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = { detail: xhr.responseText || 'Request failed' };
      }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error'));
    };
    
    xhr.send(options.body || null);
  });
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminStatus, setAdminStatus] = useState({ isAdmin: false, role: null });

  const checkAdminStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/check`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAdminStatus({ isAdmin: data.is_admin, role: data.role });
      } else {
        setAdminStatus({ isAdmin: false, role: null });
      }
    } catch {
      setAdminStatus({ isAdmin: false, role: null });
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/auth/me`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setUser(data);
      // Check admin status after confirming user is logged in
      checkAdminStatus();
    } catch {
      setUser(null);
      setAdminStatus({ isAdmin: false, role: null });
    } finally {
      setLoading(false);
    }
  }, [checkAdminStatus]);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { ok, data } = await authRequest(`${API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!ok) {
      throw new Error(formatApiError(data.detail, 'Login failed'));
    }
    setUser(data.user);
    checkAdminStatus();
    return data;
  };

  const register = async (formData) => {
    const { ok, data } = await authRequest(`${API}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    });
    if (!ok) {
      throw new Error(formatApiError(data.detail, 'Registration failed'));
    }
    setUser(data.user);
    return data;
  };

  const exchangeSession = async (sessionId) => {
    const { ok, data } = await authRequest(`${API}/api/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    });
    if (!ok) {
      throw new Error(formatApiError(data.detail, 'Session exchange failed'));
    }
    setUser(data.user);
    checkAdminStatus();
    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {}
    setUser(null);
    setAdminStatus({ isAdmin: false, role: null });
  };

  return (
    <AuthContext.Provider value={{ user, loading, adminStatus, login, register, exchangeSession, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
